//ブック削除画面
import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { useDispatch } from 'react-redux';

import { deleteBook } from '../redux/bookDbSlice';
import { deleteSheet } from '../redux/sheetDbSlice';
import { deleteSheetInformation } from '../redux/sheetInformationDbSlice';
import { deleteCard } from '../redux/cardDbSlice';
import { deleteCardAttribute } from '../redux/cardAttributeDbSlice';
import { deleteCardShape } from '../redux/cardShapeDbSlice';
import { deleteMemoCardContent } from '../redux/memoCardContentDbSlice';
import { deleteUiCardItem } from '../redux/uiCardItemDbSlice';
import { deleteDrawCardContent } from '../redux/drawCardContentDbSlice';
import { deleteArrow } from '../redux/arrowDbSlice';
import { deleteSheetTree } from '../redux/sheetTreeDbSlice';
import { deleteRelationUserBook } from '../redux/relationUserBookDbSlice';
import { deleteRelationFolderBook } from '../redux/relationFolderBookDbSlice';
import { UserCondition, updateUserCondition } from '../redux/userConditionSlice';
import { deleteNote } from '../redux/noteDbSlice';
import useSheetsDb from '../hooks/useSheetsDb';
import useAllCardDbsInBook from '../hooks/useAllCardDbsInBook';
import useCardAttributeIdsByCards from '../hooks/useCardAttributeIdsByCards';
import useCardShapeIdsByCards from '../hooks/useCardShapeIdsByCards';
import useMemoCardContentIdsByCards from '../hooks/useMemoCardContentIdsByCards';
import useUiCardItemIdsByCards from '../hooks/useUiCardItemIdsByCards';
import useDrawCardContentIdsByCards from '../hooks/useDrawCardContentIdsByCards';
import useArrowIdsBySheets from '../hooks/useArrowIdsBySheets';
import useAllRelationUserBookIdsByBook from '../hooks/useAllRelationUserBookIdsByBook';
import useAllRelationFolderBookIdsByBook from '../hooks/useAllRelationFolderBookIdsByBook';
import useAllSheetInformationIdsInBook from '../hooks/useAllSheetInformationIdsInBook';
import useAllSheetTreeIdsByBook from '../hooks/useAllSheetTreeIdsByBook';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useUserCondition from '../hooks/useUserCondition';
import useNoteDbsByReferences from '../hooks/useNoteDbsByReferences';

import { deepCopy } from '../general';

type DeleteBookDialogProps = {
  open: boolean
  onClose: () => void
  bookId: string;
  updateOrder: (id: string) => void;
}
const DeleteBookDialog: React.FC<DeleteBookDialogProps> = ({open, onClose, bookId, updateOrder}) => {
  const dispatch = useDispatch();
  const [sheetIds] = useSheetsDb(bookId);
  const sheetInformationIds = useAllSheetInformationIdsInBook(bookId);
  const [cardIds] = useAllCardDbsInBook(bookId);
  const cardAttributeIds = useCardAttributeIdsByCards(cardIds);
  const cardShapeIds = useCardShapeIdsByCards(cardIds);
  const memoContentIds = useMemoCardContentIdsByCards(cardIds);
  const uiItemIds = useUiCardItemIdsByCards(cardIds);
  const drawContentIds = useDrawCardContentIdsByCards(cardIds);
  const arrowIds = useArrowIdsBySheets(sheetIds);
  const rubIds = useAllRelationUserBookIdsByBook(bookId);
  const rfbIds = useAllRelationFolderBookIdsByBook(bookId);
  const sheetTreeIds = useAllSheetTreeIdsByBook(bookId);
  const userId = useCurrentUserId();
  const [userConditionId, userCondition] = useUserCondition(userId);
  const [noteIds] = useNoteDbsByReferences([bookId, ...sheetIds, ...cardIds]);

  const updateHashData = (condition: UserCondition, ids: string[]): UserCondition => {
    const newCondition = deepCopy(condition);
    ids.forEach(id => {
      const zoomRate = newCondition.zoomRates[id];
      const noteDisplay = newCondition.noteDisplay.bookEditor[id];
      const workNoteRatio = newCondition.workNoteRatios.bookEditor[id];
      if (zoomRate) delete newCondition.zoomRates[id];
      if (typeof noteDisplay === 'boolean') delete newCondition.noteDisplay.bookEditor[id];
      if (workNoteRatio) delete newCondition.workNoteRatios.bookEditor[id];
    })
    return newCondition;
  }

  const deleteBookExe = () => {
    updateOrder(bookId);
    sheetIds.forEach(id => dispatch(deleteSheet({id: id})));// ブック中のシートを削除する
    sheetInformationIds.forEach(id => dispatch(deleteSheetInformation({id: id})));
    cardIds.forEach(id => dispatch(deleteCard({id: id})));// ブック中のカードを削除する
    cardAttributeIds?.forEach(id => dispatch(deleteCardAttribute({id: id})));
    cardShapeIds?.forEach(id => dispatch(deleteCardShape({id: id})));
    memoContentIds?.forEach(id => dispatch(deleteMemoCardContent({id: id})));
    uiItemIds?.forEach(id => dispatch(deleteUiCardItem({id: id})));
    drawContentIds?.forEach(id => dispatch(deleteDrawCardContent({id: id})));
    arrowIds?.forEach(id => dispatch(deleteArrow({id: id})));
    sheetTreeIds.forEach(id => dispatch(deleteSheetTree({id: id})));
    rfbIds.forEach(id => dispatch(deleteRelationFolderBook({id: id})));
    rubIds.forEach(id => dispatch(deleteRelationUserBook({id: id})));
    noteIds.forEach(id => dispatch(deleteNote({ id })))

    if (userConditionId && userCondition) {
      const newCondition = updateHashData(userCondition, [bookId, ...sheetIds, ...cardIds]);
      dispatch(updateUserCondition({
        id: userConditionId,
        zoomRates: newCondition.zoomRates,
        noteDisplay: newCondition.noteDisplay,
        workNoteRatios: newCondition.workNoteRatios
      }));
    }

    dispatch(deleteBook({id: bookId}));// ブックを削除する
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>このブックを削除します</DialogTitle>
      <DialogContent />
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          キャンセル
        </Button>
        <Button color='primary' onClick={() => deleteBookExe()}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteBookDialog;
